import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { formatAmount, formatAmountInput } from '../../../utils/format-amount';
import { formatDayMonthYear } from '../../../utils/date-only';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { first, Observable } from 'rxjs';
import { Route } from '../../../utils/route';
import { select } from '../../../utils/select';
import { ShipmentState, ShipmentStore } from './shipment.store';
import { ShipmentHeader } from './models/shipment.dto';
import { isEmpty, isPlanned, ShipmentItemVm, ShipmentVm } from './models/shipment.view-model';
import { ItemPhotoComponent } from '../../item-photo/item-photo.component';

type HeaderField = keyof ShipmentHeader;

/**
 * One wysyłka: the header (DANE) and what is inside, grouped by zamówienie (ZAWARTOŚĆ).
 * Lines are added from the client-order page, where the positions and their remainders
 * already are; here they are re-amounted (double-click) and removed while the shipment
 * is Planowana, then the truck leaves with Wyślij. After that the page is read-only,
 * except for the correction at the ramp — Cofnij wysyłkę.
 */
@Component({
  selector: 'shipment-page',
  templateUrl: './shipment.page.html',
  styleUrl: './shipment.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink, ItemPhotoComponent]
})
export class ShipmentPage implements OnInit, OnDestroy {
  readonly state$: Observable<ShipmentState>;

  // the header of a shipment that does not exist yet — lives here until "Utwórz" sends it
  readonly draft = signal<ShipmentHeader>({ transport: '', plannedDate: null });

  // double-click edits, one header field or one line amount at a time (client-order precedent)
  readonly editingField = signal<HeaderField | undefined>(undefined);
  readonly editingItemId = signal<number | undefined>(undefined);
  readonly fieldInput = viewChild<ElementRef<HTMLInputElement>>('fieldInput');
  readonly amountInput = viewChild<ElementRef<HTMLInputElement>>('amountInput');

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: ShipmentStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    // the input only exists while editing — focus it as soon as it appears (overseer report-input precedent)
    effect(() => {
      const input = this.fieldInput() ?? this.amountInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Route.parseId(params.get('id'));

      this.store.init(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

      this.initParams();
    });
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  formatAmount = formatAmount;
  formatAmountInput = formatAmountInput;
  formatDayMonthYear = formatDayMonthYear;

  isPlanned = isPlanned;

  isEmpty = isEmpty;

  // --- new shipment ---

  updateDraft(field: HeaderField, value: string): void {
    this.draft.update(draft => ({ ...draft, [field]: normalize(field, value) }));
  }

  canCreate(): boolean {
    return this.draft().transport.trim().length > 0;
  }

  create(): void {
    if (!this.canCreate()) {
      return;
    }

    this.store.createShipment(this.draft()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- existing shipment header ---

  startEditing(field: HeaderField, shipment: ShipmentVm): void {
    if (isPlanned(shipment)) {
      this.editingField.set(field);
    }
  }

  stopEditing(): void {
    this.editingField.set(undefined);
  }

  saveField(field: HeaderField, value: string, current: ShipmentVm): void {
    this.stopEditing();

    const header: ShipmentHeader = {
      transport: current.transport,
      plannedDate: current.plannedDate,
      [field]: normalize(field, value)
    };

    if (!header.transport) {
      return;
    }

    this.store.patchHeader(header).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- lines ---

  startEditingAmount(item: ShipmentItemVm, shipment: ShipmentVm): void {
    if (isPlanned(shipment)) {
      this.editingItemId.set(item.shipmentItemId);
    }
  }

  stopEditingAmount(): void {
    this.editingItemId.set(undefined);
  }

  saveAmount(item: ShipmentItemVm, value: string): void {
    this.stopEditingAmount();
    this.store.updateItemAmount(item.shipmentItemId, value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  removeItem(item: ShipmentItemVm): void {
    this.store.deleteItem(item.shipmentItemId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- shipping ---

  ship(): void {
    this.store.ship().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  unship(): void {
    this.store.unship().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private initParams(): void {
    this.state$.pipe(select(state => state.shipment?.id), takeUntilDestroyed(this.destroyRef)).subscribe(shipmentId => {
      if (!shipmentId) {
        return;
      }

      this.router.navigate(['../', shipmentId], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    });
  }
}

// the optional date travels as null, never as an empty string
function normalize(field: HeaderField, value: string): string | null {
  const trimmed = value.trim();

  if (field === 'transport') {
    return trimmed;
  }

  return trimmed.length > 0 ? trimmed : null;
}
